/**
 * Smart Ranking Algorithm for Search Results
 *
 * Implements a three-tier match scoring system:
 * - Exact match (100 points): Symbol/name exactly matches query
 * - Prefix match (50 points): Symbol/name starts with query
 * - Substring match (10 points): Symbol/name contains query
 *
 * Secondary sorting by alphabetical order for tie-breaking.
 */

import type { SearchResult } from '../types';

/**
 * Calculate match score for a text string against a search query
 *
 * @param text - The text to match against (symbol or name)
 * @param query - The search query
 * @returns Object with score and match type
 */
export function calculateMatchScore(
  text: string,
  query: string,
): { score: number; matchType: 'exact' | 'prefix' | 'substring' } {
  if (!text || !query) {
    return { score: 0, matchType: 'substring' };
  }

  const lowerText = text.toLowerCase().trim();
  const lowerQuery = query.toLowerCase().trim();

  // Empty query after trimming
  if (!lowerQuery) {
    return { score: 0, matchType: 'substring' };
  }

  // Exact match: "btc" === "btc"
  if (lowerText === lowerQuery) {
    return { score: 100, matchType: 'exact' };
  }

  // Prefix match: "btc/usdt" starts with "btc"
  if (lowerText.startsWith(lowerQuery)) {
    return { score: 50, matchType: 'prefix' };
  }

  // Substring match: "wbtc/usdt" contains "btc"
  if (lowerText.includes(lowerQuery)) {
    return { score: 10, matchType: 'substring' };
  }

  // No match
  return { score: 0, matchType: 'substring' };
}

/**
 * Rank search results by match score and alphabetical order
 *
 * @param results - Array of search results to rank
 * @param query - The search query
 * @returns Filtered and sorted array of search results with matchScore and matchType
 */
export function rankSearchResults<T extends SearchResult>(results: T[], query: string): T[] {
  if (!results || results.length === 0) {
    return [];
  }

  if (!query || !query.trim()) {
    return [];
  }

  return results
    .map(result => {
      const candidates: string[] = [];

      switch (result.type) {
        case 'pair':
          candidates.push(
            result.symbol,
            result.projectName,
            String(result.pairId),
            String(result.projectId),
            result.platform,
            result.pairType,
          );
          // Add token candidates if available
          if ('baseToken' in result && result.baseToken) {
            candidates.push(result.baseToken);
          }
          if ('quoteToken' in result && result.quoteToken) {
            candidates.push(result.quoteToken);
          }
          break;
        case 'project':
          candidates.push(result.name, String(result.projectId));
          break;
        case 'arbitrage':
          candidates.push(result.name, String(result.arbitrageId), String(result.projectId));
          break;
        case 'balancer':
          candidates.push(result.name, String(result.balancerId), String(result.projectId));
          break;
      }

      let bestScore = 0;
      let bestMatchType: 'exact' | 'prefix' | 'substring' = 'substring';

      candidates.forEach(candidate => {
        const { score, matchType } = calculateMatchScore(candidate, query);
        if (score > bestScore) {
          bestScore = score;
          bestMatchType = matchType;
        }
      });

      return {
        ...result,
        matchScore: bestScore,
        matchType: bestMatchType,
      };
    })
    .filter(result => result.matchScore > 0)
    .sort((a, b) => {
      if (b.matchScore !== a.matchScore) {
        return b.matchScore - a.matchScore;
      }

      const getDisplayText = (result: SearchResult): string => {
        switch (result.type) {
          case 'pair':
            return result.symbol;
          case 'project':
          case 'arbitrage':
          case 'balancer':
            return result.name;
          default: {
            // Exhaustive check - ensures all SearchResult types are handled
            const _exhaustiveCheck: never = result;
            return String(_exhaustiveCheck);
          }
        }
      };
      const aText = getDisplayText(a);
      const bText = getDisplayText(b);
      return aText.localeCompare(bText);
    });
}

/**
 * Group search results by category with max results per category
 *
 * @param results - Mixed array of search results
 * @param maxPerCategory - Maximum results to return per category (default: 5)
 * @returns Object with results grouped by category
 */
export function groupResultsByCategory<T extends SearchResult>(
  results: T[],
  maxPerCategory = 5,
): {
  projects: T[];
  pairs: T[];
  arbitrages: T[];
  balancers: T[];
} {
  const grouped = {
    projects: [] as T[],
    pairs: [] as T[],
    arbitrages: [] as T[],
    balancers: [] as T[],
  };

  results.forEach(result => {
    switch (result.type) {
      case 'project':
        if (grouped.projects.length < maxPerCategory) {
          grouped.projects.push(result);
        }
        break;
      case 'pair':
        if (grouped.pairs.length < maxPerCategory) {
          grouped.pairs.push(result);
        }
        break;
      case 'arbitrage':
        if (grouped.arbitrages.length < maxPerCategory) {
          grouped.arbitrages.push(result);
        }
        break;
      case 'balancer':
        if (grouped.balancers.length < maxPerCategory) {
          grouped.balancers.push(result);
        }
        break;
    }
  });

  return grouped;
}
